import React, { useEffect } from 'react';

const SEO = ({
  title = "Nike 3D Store - Premium Athletic Footwear & Sportswear | Just Do It",
  description = "Discover Nike's premium collection of athletic footwear, sportswear, and accessories. Experience our revolutionary 3D product showcase featuring Air Max, Jordan, React, and more. Shop the latest Nike innovations with fast shipping and authentic guarantee.",
  keywords = "Nike, athletic shoes, sportswear, Air Max, Jordan, React, running shoes, basketball shoes, lifestyle sneakers, Nike store, athletic apparel, sports gear, Just Do It",
  canonicalUrl = "https://nike3d.com/",
  ogImage = "/og-image.jpg",
  twitterImage = "/twitter-image.jpg",
  structuredData = null,
  noIndex = false,
  article = false,
  product = null
}) => {
  // Generate product structured data if product is provided
  const generateProductStructuredData = (product) => {
    if (!product) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": product.image,
      "brand": {
        "@type": "Brand",
        "name": "Nike"
      },
      "offers": {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Nike 3D Store"
        }
      },
      "aggregateRating": product.rating ? {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "reviewCount": product.reviews || 0,
        "bestRating": 5,
        "worstRating": 1
      } : undefined,
      "category": product.category,
      "sku": `NIKE-${product.id}`,
      "gtin": `0123456${String(product.id).padStart(6, '0')}`
    };
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbData = (breadcrumbs) => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  };

  const robotsContent = noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update or create meta tags
    const updateMetaTag = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Update canonical link
    const updateCanonicalLink = (url) => {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', url);
    };
    
    // Update structured data
    const updateStructuredData = (data, id) => {
      // Remove existing structured data with this id
      const existing = document.querySelector(`script[data-seo-id="${id}"]`);
      if (existing) {
        existing.remove();
      }
      
      if (data) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-id', id);
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      }
    };
    
    // Update basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', robotsContent);
    
    // Update Open Graph tags
    updateMetaTag('og:type', article ? "article" : "website", true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:image', `https://nike3d.com${ogImage}`, true);
    updateMetaTag('og:image:alt', title, true);
    updateMetaTag('og:site_name', 'Nike 3D Store', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', `https://nike3d.com${twitterImage}`);
    updateMetaTag('twitter:image:alt', title);
    updateMetaTag('twitter:site', '@nike');
    updateMetaTag('twitter:creator', '@nike');
    
    // Update canonical URL
    updateCanonicalLink(canonicalUrl);
    
    // Update structured data
    if (structuredData) {
      updateStructuredData(structuredData, 'custom');
    }
    
    if (product) {
      updateStructuredData(generateProductStructuredData(product), 'product');
    }
    
    // Article specific tags
    if (article) {
      updateMetaTag('article:publisher', 'https://www.facebook.com/nike', true);
      updateMetaTag('article:author', 'Nike 3D Store', true);
      updateMetaTag('article:published_time', new Date().toISOString(), true);
      updateMetaTag('article:modified_time', new Date().toISOString(), true);
    }
    
  }, [title, description, keywords, canonicalUrl, ogImage, twitterImage, structuredData, product, article, noIndex, robotsContent]);

  return null; // This component doesn't render anything visible
};

export default SEO;
